/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import {
  DisplayCheckBox,
  TableDataInfo,
  ValueDataObject,
} from '@g17eco/types/questionInterfaces';
import { isDisabled } from '../form/input/table/tableUtils';
import { recalculateVisibilityRules, isColumnVisible, recalculateDisabledRules, isColumnDisabled } from '../form/input/table/calculation';
import { UniversalTrackerValuePlain, ValueData, ValueHistory } from '@g17eco/types/universalTrackerValue';
import { getGroup, standards } from '@g17eco/core';
import { getTableConfiguration, isCheckboxType } from '@utils/universalTracker';
import { getValueData } from '@utils/universalTrackerValue';
import { UtrValueType } from '@g17eco/types/universalTracker';
import UniversalTrackerClassInterface from '../../../model/UniversalTrackerClassInterface';

export interface DataCheck {
  utr: UniversalTrackerClassInterface | undefined;
  displayCheckbox: { [p: string]: boolean };
  valueData: ValueData;
  value: number | undefined;
  table: TableDataInfo | undefined;
  shouldEnforceTable?: boolean;
}

export const isDataEmpty = ({ utr, displayCheckbox, valueData, value, table, shouldEnforceTable }: DataCheck) => {
  if (!utr) {
    return true;
  }

  const valueType = shouldEnforceTable ? UtrValueType.Table : utr.getValueType();

  switch (valueType) {
    case 'text':
    case 'date':
    case 'valueList':
      return !valueData.data || typeof valueData.data !== 'string';
    case 'valueListMulti':
      return !valueData.data || typeof valueData.data !== 'object' || !Array.isArray(valueData.data) || valueData.data.length === 0;
    case 'numericValueList': {
      if (!valueData.data) {
        return true;
      }
      const d = valueData.data as ValueDataObject;
      // Selected
      const keys: string[] = Object.keys(displayCheckbox).filter((k) => displayCheckbox[k]);
      // It's empty checkbox or there is at least one empty value
      return keys.length === 0 || keys.some((k: string) => isNaN(d[k] as number));
    }
    case 'textValueList': {
      if (!valueData.data) {
        return true;
      }
      const d = valueData.data as ValueDataObject; // { lll: 'yes what ever' }
      // Selected
      const keys: string[] = Object.keys(displayCheckbox).filter((k) => displayCheckbox[k]);
      // It's empty checkbox or there is at least one empty value
      return keys.length === 0 || keys.some((k: string) => !d[k]);
    }
    case 'number':
    case 'percentage':
      return value === undefined || isNaN(value);
    // NOTE: Unlike other value types that only check "is data present", table validation
    // also checks required fields and min/max constraints via isDisabled().
    // This is intentional because some consumers (SetDataForm, SetupView) rely solely
    // on isDataEmpty() for validation without a separate inputMessage validation layer.
    // Visibility and disabled rules are applied to skip validation for hidden/disabled columns (GU-6730).
    case 'table': {
      if (table) {
        if (!Array.isArray(table.rows) || table.rows.filter((r) => !r.isRemoved).length === 0) {
          return true;
        }

        const tableConfiguration = getTableConfiguration(utr, shouldEnforceTable);
        const cols = tableConfiguration?.columns;

        if (cols) {
          const visibilityRuleColumns = cols.filter((c) => c.visibilityRule?.formula);
          const disabledRuleColumns = cols.filter((c) => c.disabledRule?.formula);
          return table.rows.some((row) => {
            const { visibleResults } = recalculateVisibilityRules(row.data, visibilityRuleColumns, {});
            const { disabledResults } = recalculateDisabledRules(row.data, disabledRuleColumns, {});
            const validatableCols = cols.filter((col) =>
              isColumnVisible(col.code, visibleResults) && !isColumnDisabled(col.code, disabledResults)
            );
            return isDisabled(validatableCols, row.data);
          });
        }
      }
      return !Array.isArray(valueData.table) || valueData.table.length === 0;
    }
    default:
      return true;
  }
};

export const checkHasComments = (
  stakeholderHistory: Pick<ValueHistory, 'note'>,
  verifierHistory: Pick<ValueHistory, 'note'>
) => {
  const combinedHistory = [stakeholderHistory, verifierHistory];
  return combinedHistory.some((c) => Boolean(c?.note));
};

/**
 * Retrieves the latest alternative code for a framework.
 */

export const getLatestAlternativeCode = ({ utr }: { utr: UniversalTrackerClassInterface }) => {
  let currentStandard = getGroup('standards', utr.getType());
  const versionGroupCode = currentStandard?.versionGroupCode;
  if (!currentStandard || !versionGroupCode) {
    return utr.getType();
  }

  for (const standard of Object.values(standards)) {
    if (
      standard.versionGroupCode === versionGroupCode &&
      Number(standard.version) > Number(currentStandard.version) &&
      utr.hasAlternativeInfo(standard.code)
    ) {
      currentStandard = standard;
    }
  }

  return currentStandard.code;
};

export const getDisplayCheckboxAndOriginalValueData = ({
  valueType,
  utrv,
}: {
  valueType: UtrValueType;
  utrv: Pick<UniversalTrackerValuePlain, 'valueData'>;
}) => {
  const displayCheckbox: DisplayCheckBox = {};
  const originalValueData = getValueData(utrv, valueType);

  if (isCheckboxType(valueType) && originalValueData.data) {
    Object.keys(originalValueData.data).forEach((k) => (displayCheckbox[k] = true));
  }

  return { displayCheckbox, originalValueData };
};
